/**
 * @fileoverview Popover for displaying organization members and enabling role updates.
 * @module features/settings/components/OrgMembersCard
 */

import { ReactElement, useState } from 'react'
import { X } from 'lucide-react'
import {
  Button,
  Input,
  Label,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from '@/shared/components/ui'
import { PopoverContent } from '@/components/ui/popover'
import { Skeleton } from '@/components/ui/skeleton'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/lib/apiClient'
import { z } from 'zod'
import { OrgIdSchema } from '@/features/orgs/schemas/orgSchemas'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { inviteMember } from '@/features/orgs/api/invitesService'

/** Strict role enumeration for org members. */
const MemberRoleSchema = z.enum(['OWNER', 'ADMIN', 'MEMBER'])
type MemberRole = z.infer<typeof MemberRoleSchema>

/** Member item as returned by /orgs/{orgId}/members. */
const MemberItemSchema = z.object({ userId: z.string().uuid(), role: MemberRoleSchema, email: z.string().email().nullable().optional() })
type MemberItem = z.infer<typeof MemberItemSchema>

/** Normalize various member list response shapes to MemberItem[]. */
function normalizeMembers(payload: unknown): MemberItem[] | null {
  const arr = z.array(MemberItemSchema).safeParse(payload)
  if (arr.success) return arr.data
  const members = z.object({ members: z.array(MemberItemSchema) }).safeParse(payload)
  if (members.success) return members.data.members
  const content = z.object({ content: z.array(MemberItemSchema) }).safeParse(payload)
  if (content.success) return content.data.content
  const data = z.object({ data: z.array(MemberItemSchema) }).safeParse(payload)
  if (data.success) return data.data.data
  return null
}

export interface OrgMembersPopoverProps {
  /** Organization ID to fetch members for */
  orgId: string
  /** Current user's ID for self-edit checks */
  currentUserId: string
  /** Current user's role in this organization */
  currentUserRole: MemberRole | undefined
  /** Additional CSS classes for PopoverContent */
  className?: string
}

/**
 * Popover content for displaying and managing organization members.
 * Must be used within a Popover component context.
 *
 * @component
 * @returns ReactElement
 * @example
 * ```tsx
 * <Popover open={isOpen} onOpenChange={setIsOpen}>
 *   <PopoverTrigger>View Members</PopoverTrigger>
 *   <OrgMembersPopover orgId={orgId} currentUserId={userId} currentUserRole={role} />
 * </Popover>
 * ```
 */
export function OrgMembersPopover({
  orgId,
  currentUserId,
  currentUserRole,
  className,
}: OrgMembersPopoverProps): ReactElement {
  const { t } = useTranslation('features-settings')
  const [inviteOpen, setInviteOpen] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<MemberRole>('MEMBER')
  const [inviteLink, setInviteLink] = useState<string | null>(null)
  const [removeTarget, setRemoveTarget] = useState<string | null>(null)

  const orgIdStr = OrgIdSchema.parse(orgId) as unknown as string

  const membersQuery = useQuery<MemberItem[], Error>({
    queryKey: ['org', 'members', orgIdStr ?? 'no-org'],
    enabled: Boolean(orgIdStr),
    queryFn: async () => {
      if (!orgIdStr) throw new Error('No active organization')
      const res = await apiClient.get(`/orgs/${orgIdStr}/members`)
      const normalized = normalizeMembers(res.data)
      if (!normalized) throw new Error('Invalid members response')
      return normalized
    },
    staleTime: 60 * 1000,
  })

  const qc = useQueryClient()
  const updateRole = useMutation({
    mutationFn: async (payload: { memberId: string; role: MemberRole }) => {
      if (!orgIdStr) throw new Error('No active organization')
      await apiClient.patch(`/orgs/${orgIdStr}/members/${payload.memberId}`, { role: payload.role })
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['org', 'members', orgIdStr ?? 'no-org'] })
      toast.success(t('cards.members.toasts.roleUpdated'))
    },
    onError: (e: unknown) => toast.error((e as Error)?.message ?? t('cards.members.errors.updateRole')),
  })

  const removeMember = useMutation({
    mutationFn: async (memberId: string) => {
      if (!orgIdStr) throw new Error('No active organization')
      await apiClient.delete(`/orgs/${orgIdStr}/members/${memberId}`)
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['org', 'members', orgIdStr ?? 'no-org'] })
      toast.success(t('cards.members.toasts.memberRemoved'))
    },
    onError: (e: unknown) => toast.error((e as Error)?.message ?? t('cards.members.errors.removeMember')),
  })

  const canEdit = currentUserRole === 'OWNER' || currentUserRole === 'ADMIN'

  return (
    <PopoverContent className={`w-[560px] rounded-xl border bg-popover p-6 shadow-2xl ${className || ''}`} align="end" sideOffset={8}>
      <div className="mb-4 text-lg font-semibold text-foreground">{t('cards.members.title')}</div>
      {membersQuery.isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-5 w-44" />
          <Skeleton className="h-5 w-64" />
          <Skeleton className="h-5 w-56" />
        </div>
      ) : membersQuery.error ? (
        <div className="text-sm text-foreground">{t('cards.members.errors.loadMembers')}</div>
      ) : membersQuery.data?.length ? (
        <>
          <ul className="space-y-3 max-h-[400px] overflow-y-auto mb-4">
            {membersQuery.data.map(m => {
              const isSelf = currentUserId === m.userId
              const isOwnerRow = m.role === 'OWNER'
              const disabled =
                !canEdit || updateRole.isPending || isSelf || (currentUserRole === 'ADMIN' && isOwnerRow)
              const options: MemberRole[] =
                currentUserRole === 'ADMIN' ? ['ADMIN', 'MEMBER'] : ['OWNER', 'ADMIN', 'MEMBER']

              // OWNER can remove ADMIN/MEMBER; ADMIN can remove MEMBER only
              const canRemove =
                !isSelf &&
                !removeMember.isPending &&
                ((currentUserRole === 'OWNER' && m.role !== 'OWNER') ||
                  (currentUserRole === 'ADMIN' && m.role === 'MEMBER'))

              return (
                <li key={m.userId} className="border border-border rounded-md p-3">
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-xs text-muted-foreground">
                          {t('cards.members.labels.user')}
                        </div>
                        <div className="text-sm text-foreground">{m.email ?? m.userId}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        {canEdit && (
                          <select
                            className="h-8 rounded-md border border-border bg-background px-2 text-sm text-foreground"
                            defaultValue={m.role}
                            onChange={e => {
                              const next = MemberRoleSchema.parse(e.target.value)
                              if (next === m.role) return
                              updateRole.mutate({ memberId: m.userId, role: next })
                            }}
                            disabled={disabled}
                            aria-label={t('cards.members.aria.roleSelect')}
                          >
                            {options.map(r => (
                              <option key={r} value={r}>
                                {r}
                              </option>
                            ))}
                          </select>
                        )}
                        {!canEdit && (
                          <div className="text-xs text-muted-foreground">{m.role}</div>
                        )}
                        {canRemove && (
                          <button
                            type="button"
                            className="rounded-xs p-1 text-muted-foreground opacity-70 transition-opacity hover:opacity-100 hover:text-destructive"
                            onClick={() => setRemoveTarget(m.userId)}
                            aria-label={t('cards.members.remove.button')}
                          >
                            <X className="size-4" />
                          </button>
                        )}
                      </div>
                    </div>
                    {isSelf && (
                      <div className="text-xs text-muted-foreground">
                        {t('cards.members.hints.noSelfEdit')}
                      </div>
                    )}
                    {currentUserRole === 'ADMIN' && isOwnerRow && (
                      <div className="text-xs text-muted-foreground">
                        {t('cards.members.hints.adminCannotEditOwner')}
                      </div>
                    )}
                  </div>
                </li>
              )
            })}
          </ul>
          {canEdit && (
            <div className="flex justify-end">
              <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
                <DialogTrigger asChild>
                  <Button type="button" variant="outline" size="sm">
                    {t('cards.members.invite.button')}
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-sm">
                  <DialogHeader>
                    <DialogTitle>{t('cards.members.invite.dialogTitle')}</DialogTitle>
                    <DialogDescription>
                      {t('cards.members.invite.dialogDescription')}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="grid gap-4 py-2">
                    {inviteLink ? (
                      <div className="grid gap-2">
                        <Label htmlFor="invite-link">{t('cards.members.invite.linkLabel')}</Label>
                        <div className="flex gap-2">
                          <Input id="invite-link" value={inviteLink} readOnly className="flex-1" />
                          <Button
                            type="button"
                            size="sm"
                            onClick={async () => {
                              try {
                                await navigator.clipboard.writeText(inviteLink)
                                toast.success(t('cards.members.invite.linkCopied'))
                              } catch {
                                toast.error(t('cards.members.invite.copyFailed'))
                              }
                            }}
                          >
                            {t('cards.members.invite.copyButton')}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="grid gap-2">
                          <Label htmlFor="invite-email">{t('cards.members.invite.emailLabel')}</Label>
                          <Input
                            id="invite-email"
                            type="email"
                            placeholder={t('cards.members.invite.emailPlaceholder')}
                            value={inviteEmail}
                            onChange={e => setInviteEmail(e.target.value)}
                          />
                        </div>
                        <div className="grid gap-2">
                          <Label htmlFor="invite-role">{t('cards.members.invite.roleLabel')}</Label>
                          <select
                            id="invite-role"
                            className="h-9 rounded-md border border-border bg-background px-2 text-foreground"
                            value={inviteRole}
                            onChange={e => setInviteRole(MemberRoleSchema.parse(e.target.value))}
                          >
                            {(currentUserRole === 'ADMIN'
                              ? ['ADMIN', 'MEMBER']
                              : ['OWNER', 'ADMIN', 'MEMBER']
                            ).map(r => (
                              <option key={r} value={r}>
                                {r}
                              </option>
                            ))}
                          </select>
                        </div>
                      </>
                    )}
                  </div>
                  <DialogFooter>
                    {inviteLink ? (
                      <Button type="button" onClick={() => setInviteOpen(false)}>
                        {t('cards.members.invite.closeButton')}
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        onClick={async () => {
                          try {
                            const email = z.string().email(t('cards.members.invite.invalidEmail')).parse(inviteEmail)
                            const role = MemberRoleSchema.parse(inviteRole)
                            const { link } = await inviteMember(OrgIdSchema.parse(orgId), {
                              email,
                              role,
                            })
                            setInviteLink(link)
                            toast.success(t('cards.members.invite.created'))
                          } catch (e) {
                            toast.error((e as Error)?.message ?? t('cards.members.invite.createFailed'))
                          }
                        }}
                        disabled={!inviteEmail}
                      >
                        {t('cards.members.invite.generateButton')}
                      </Button>
                    )}
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          )}
        </>
      ) : (
        <div className="text-sm text-foreground">{t('cards.members.empty.noMembers')}</div>
      )}
      <Dialog open={removeTarget !== null} onOpenChange={open => { if (!open) setRemoveTarget(null) }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('cards.members.remove.dialogTitle')}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {t('cards.members.remove.dialogDescription')}
          </p>
          <DialogFooter className="flex flex-row justify-end gap-2">
            <Button
              type="button"
              variant="destructive"
              disabled={removeMember.isPending}
              onClick={() => {
                if (removeTarget) {
                  removeMember.mutate(removeTarget, {
                    onSettled: () => setRemoveTarget(null),
                  })
                }
              }}
            >
              {removeMember.isPending
                ? t('cards.members.remove.removing')
                : t('cards.members.remove.button')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PopoverContent>
  )
}

/** Backward compatibility export - OrgMembersPopover is the main component */
export { OrgMembersPopover as OrgMembersCard }
